<template>
  <div class="min-h-screen bg-[#FAF8F8] px-4 sm:px-6 lg:px-8 py-8">
    <div class="max-w-6xl mx-auto space-y-6">

      <!-- Lien Retour -->
      <div>
        <router-link to="/projects"
          class="inline-flex items-center gap-1.5 text-xs font-bold text-[#F97316] hover:text-[#ea580c] transition group">
          <svg class="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor"
            viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour aux projets
        </router-link>
      </div>

      <!-- État : Chargement -->
      <div v-if="isLoading"
        class="bg-white rounded-2xl p-16 border border-gray-100 text-center flex flex-col items-center gap-3">
        <svg class="animate-spin h-8 w-8 text-[#7C3AED]" xmlns="http://www.w3.org/2000/svg" fill="none"
          viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
        </svg>
        <span class="text-sm font-medium text-gray-500">Chargement de l'espace projet...</span>
      </div>

      <!-- État : Erreur -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 p-6 rounded-2xl text-sm font-medium">
        {{ error }}
      </div>

      <!-- Contenu du Projet -->
      <template v-else-if="project">
        <!-- Carte En-tête Projet -->
        <section
          class="bg-white rounded-2xl shadow-xs p-6 sm:p-8 border border-gray-100 flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div class="space-y-2 max-w-2xl">
            <div class="flex items-center gap-3 flex-wrap">
              <span class="font-mono text-xs font-bold text-gray-400">#{{ project.id }}</span>
              <h1 class="text-2xl sm:text-3xl font-extrabold text-[#0B1B3D] tracking-tight">
                {{ project.title }}
              </h1>
              <span class="text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap"
                :class="typeBadgeClass(project.project_type)">
                {{ project.project_type || 'Général' }}
              </span>
            </div>
            <p class="text-gray-600 text-sm leading-relaxed pt-1">
              {{ project.description || 'Aucune description fournie pour ce projet.' }}
            </p>
          </div>

          <!-- Sélecteur de statut dans l'en-tête -->
          <div class="flex flex-col sm:items-end gap-2 shrink-0">
            <label class="text-[11px] uppercase tracking-wider font-semibold text-gray-400">Statut du projet</label>
            <select :value="project.status" @change="updateProjectStatus($event.target.value)"
              class="text-xs font-bold px-3.5 py-2 rounded-xl border-0 outline-none cursor-pointer transition shadow-xs"
              :class="statusBadgeClass(project.status)">
              <option v-for="s in STATUSES" :key="s" :value="s">{{ s }}</option>
            </select>
          </div>
        </section>

        <!-- Grille Principale : Lots & Livrables -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

          <!-- Colonne 1 & 2 : Liste des Lots du projet -->
          <div class="lg:col-span-2 space-y-6">
            <section class="bg-white rounded-2xl shadow-xs p-6 border border-gray-100">
              <div class="flex items-center justify-between mb-6">
                <div>
                  <h2 class="text-lg font-bold text-[#0B1B3D]">Lots & Livrables de production</h2>
                  <p class="text-xs text-gray-400 mt-0.5">Structurez votre projet en étapes mesurables</p>
                </div>
                <span class="text-xs font-bold px-2.5 py-1 bg-purple-50 text-[#7C3AED] rounded-lg">
                  {{ lots.length }} lot(s)
                </span>
              </div>

              <!-- Liste vide -->
              <div v-if="lots.length === 0"
                class="border-2 border-dashed border-gray-100 rounded-2xl p-10 text-center text-gray-400 text-sm">
                Aucun lot pour ce projet pour le moment.
              </div>

              <!-- Liste des lots -->
              <div v-else class="space-y-3">
                <div v-for="lot in lots" :key="lot.id"
                  class="bg-[#FAF8F8] hover:bg-white border border-gray-100 hover:border-[#7C3AED]/30 rounded-xl p-4 transition-all duration-150 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="w-2 h-2 rounded-full bg-[#7C3AED]"></span>
                      <h3 class="text-sm font-bold text-[#0B1B3D] truncate">{{ lot.name }}</h3>
                    </div>
                    <p v-if="lot.description" class="text-xs text-gray-500 mt-1 pl-4">
                      {{ lot.description }}
                    </p>
                    <p v-if="lot.start_date || lot.end_date"
                      class="text-[11px] text-gray-400 mt-1.5 pl-4 flex items-center gap-1.5">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {{ formatDate(lot.start_date) }} → {{ formatDate(lot.end_date) }}
                    </p>
                  </div>

                  <!-- Statut du lot -->
                  <div class="sm:self-center shrink-0">
                    <select :value="lot.status" @change="updateLotStatus(lot, $event.target.value)"
                      class="text-xs font-semibold px-2.5 py-1.5 rounded-lg border-0 outline-none cursor-pointer"
                      :class="statusBadgeClass(lot.status)">
                      <option v-for="s in STATUSES" :key="s" :value="s">{{ s }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <!-- Colonne 3 : Ajout d'un Lot & Résumé -->
          <div class="space-y-6">
            <!-- Formulaire d'ajout rapide de lot -->
            <section class="bg-white rounded-2xl shadow-xs p-6 border border-gray-100">
              <h2 class="text-base font-bold text-[#0B1B3D] mb-4">+ Ajouter une étape / lot</h2>

              <form @submit.prevent="addLot" class="flex flex-col gap-3">
                <div>
                  <label class="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Nom du
                    lot</label>
                  <input v-model="newLot.name" placeholder="ex: Maquettes UI, Déploiement..." required
                    class="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none transition" />
                </div>

                <div>
                  <label
                    class="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</label>
                  <input v-model="newLot.description" placeholder="Objectif du livrable..."
                    class="w-full text-xs p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none transition" />
                </div>

                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label
                      class="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Début</label>
                    <input v-model="newLot.start_date" type="date"
                      class="w-full text-xs p-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] outline-none" />
                  </div>
                  <div>
                    <label
                      class="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Fin</label>
                    <input v-model="newLot.end_date" type="date"
                      class="w-full text-xs p-2 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#7C3AED] outline-none" />
                  </div>
                </div>

                <button type="submit" :disabled="isCreatingLot"
                  style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
                  class="mt-2 text-white font-bold py-2.5 px-4 rounded-xl shadow-sm hover:opacity-95 transition disabled:opacity-50 text-xs">
                  {{ isCreatingLot ? 'Ajout en cours...' : 'Créer le lot' }}
                </button>
              </form>
            </section>
          </div>

        </div>
      </template>

    </div>
  </div>
</template>

<script>
import apiClient from '@/services/apiClient'

const STATUSES = [
  'Brouillon',
  'En cours',
  'En révision',
  'Terminé',
  'En maintenance'
]

const STATUS_COLORS = {
  Brouillon: 'bg-gray-100 text-gray-600',
  'En cours': 'bg-orange-100 text-[#F97316]',
  'En révision': 'bg-purple-100 text-[#7C3AED]',
  Terminé: 'bg-emerald-100 text-emerald-700 font-bold',
  'En maintenance': 'bg-fuchsia-100 text-[#C026D3]'
}

const TYPE_COLORS = [
  'bg-purple-50 text-[#7C3AED] border border-purple-200',
  'bg-fuchsia-50 text-[#C026D3] border border-fuchsia-200',
  'bg-orange-50 text-[#F97316] border border-orange-200',
  'bg-amber-50 text-amber-700 border border-amber-200',
  'bg-blue-50 text-blue-700 border border-blue-200',
  'bg-emerald-50 text-emerald-700 border border-emerald-200'
]

export default {
  name: 'ProjectDetailView',
  data() {
    return {
      project: null,
      lots: [],
      isLoading: false,
      error: null,
      newLot: { name: '', description: '', start_date: '', end_date: '' },
      isCreatingLot: false,
      STATUSES
    }
  },
  methods: {
    async fetchProjectDetails() {
      const projectId = this.$route.params.id
      this.isLoading = true
      this.error = null

      try {
        const [projectRes, lotsRes] = await Promise.all([
          apiClient.get(`/api/v1/projects/${projectId}`),
          apiClient.get(`/api/v1/projects/${projectId}/lots`).catch(() => ({ data: [] }))
        ])

        this.project = projectRes.data?.project || projectRes.data
        this.lots = lotsRes.data || []
      } catch (err) {
        console.error(err)
        this.error = 'Impossible de charger les détails du projet.'
      } finally {
        this.isLoading = false
      }
    },
    async updateProjectStatus(newStatus) {
      if (!this.project || this.project.status === newStatus) return
      const oldStatus = this.project.status
      this.project.status = newStatus

      try {
        await apiClient.patch(`/api/v1/projects/${this.project.id}`, {
          project: { status: newStatus }
        })
      } catch (err) {
        this.project.status = oldStatus
        console.error(err)
      }
    },
    async addLot() {
      if (!this.project) return
      this.isCreatingLot = true

      try {
        const res = await apiClient.post(`/api/v1/projects/${this.project.id}/lots`, {
          lot: this.newLot
        })
        if (res.data) {
          this.lots.push(res.data?.lot || res.data)
        }
        this.newLot = { name: '', description: '', start_date: '', end_date: '' }
        // Rafraîchir
        await this.fetchProjectDetails()
      } catch (err) {
        console.error(err)
      } finally {
        this.isCreatingLot = false
      }
    },
    async updateLotStatus(lot, newStatus) {
      lot.status = newStatus
      try {
        await apiClient.patch(`/api/v1/projects/${this.project.id}/lots/${lot.id}`, {
          lot: { status: newStatus }
        })
      } catch (err) {
        console.error(err)
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '?'
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },
    statusBadgeClass(status) {
      return STATUS_COLORS[status] || STATUS_COLORS.Brouillon
    },
    typeBadgeClass(type) {
      const label = type || 'Général'
      let hash = 0
      for (let i = 0; i < label.length; i++) {
        hash = (hash * 31 + label.charCodeAt(i)) % TYPE_COLORS.length
      }
      return TYPE_COLORS[Math.abs(hash)]
    }
  },
  mounted() {
    this.fetchProjectDetails()
  }
}
</script>
